/**
 * Filter bar for ActivityBrowser.
 *
 * Mirrors `FeedbackFilters` style — a single horizontal strip with
 * date pickers, free-text inputs, and dropdowns. Empty strings
 * are treated as "no filter" so the parent's stripUndefined()
 * pass drops them from the wire payload.
 */

import type { ActivityCategory, ActivitySource } from "@lexiun/lx-obs";

export interface ActivityFilterState {
  /** ISO date string (YYYY-MM-DD) — converted to epoch ms by the parent. */
  fromDate: string;
  toDate: string;
  user_id: string;
  session_id: string;
  case_id: string;
  agent_id: string;
  task_id: string;
  source: "" | ActivitySource;
  subsystem: string;
  category: "" | ActivityCategory;
  type: string;
  errorsOnly: boolean;
}

export const EMPTY_FILTER: ActivityFilterState = {
  fromDate: defaultFromDate(),
  toDate: defaultToDate(),
  user_id: "",
  session_id: "",
  case_id: "",
  agent_id: "",
  task_id: "",
  source: "",
  subsystem: "",
  category: "",
  type: "",
  errorsOnly: false,
};

function defaultFromDate(): string {
  // Last 7 days by default — matches Dashboard's heatmap window.
  const d = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  return d.toISOString().slice(0, 10);
}
function defaultToDate(): string {
  return new Date().toISOString().slice(0, 10);
}

const SOURCES: Array<{ value: "" | ActivitySource; label: string }> = [
  { value: "", label: "Any source" },
  { value: "lexide", label: "lexide" },
  { value: "lexruntime", label: "lexruntime" },
];

const CATEGORIES: Array<{ value: "" | ActivityCategory; label: string }> = [
  { value: "", label: "Any category" },
  { value: "navigation", label: "navigation" },
  { value: "interaction", label: "interaction" },
  { value: "form", label: "form" },
  { value: "prompt", label: "prompt" },
  { value: "llm", label: "llm" },
  { value: "tool", label: "tool" },
  { value: "agent", label: "agent" },
  { value: "session", label: "session" },
  { value: "message", label: "message" },
  { value: "gateway", label: "gateway" },
  { value: "error", label: "error" },
  { value: "lifecycle", label: "lifecycle" },
];

interface Props {
  value: ActivityFilterState;
  onChange: (next: ActivityFilterState) => void;
}

export function ActivityFilters({ value, onChange }: Props) {
  const set = <K extends keyof ActivityFilterState>(
    key: K,
    v: ActivityFilterState[K],
  ) => onChange({ ...value, [key]: v });

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="From">
          <input
            type="date"
            value={value.fromDate}
            onChange={(e) => set("fromDate", e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="To">
          <input
            type="date"
            value={value.toDate}
            onChange={(e) => set("toDate", e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Source">
          <select
            value={value.source}
            onChange={(e) =>
              set("source", e.target.value as ActivityFilterState["source"])
            }
            className={inputClass}
          >
            {SOURCES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Category">
          <select
            value={value.category}
            onChange={(e) =>
              set("category", e.target.value as ActivityFilterState["category"])
            }
            className={inputClass}
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Subsystem">
          <input
            type="text"
            placeholder="e.g. harn, plugin"
            value={value.subsystem}
            onChange={(e) => set("subsystem", e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Type">
          <input
            type="text"
            placeholder="e.g. harness.task.start"
            value={value.type}
            onChange={(e) => set("type", e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="User id">
          <input
            type="text"
            value={value.user_id}
            onChange={(e) => set("user_id", e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Session id">
          <input
            type="text"
            value={value.session_id}
            onChange={(e) => set("session_id", e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Case id">
          <input
            type="text"
            value={value.case_id}
            onChange={(e) => set("case_id", e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Agent id">
          <input
            type="text"
            value={value.agent_id}
            onChange={(e) => set("agent_id", e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Task id">
          <input
            type="text"
            value={value.task_id}
            onChange={(e) => set("task_id", e.target.value)}
            className={inputClass}
          />
        </Field>
        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={value.errorsOnly}
            onChange={(e) => set("errorsOnly", e.target.checked)}
          />
          Errors only
        </label>
        <button
          type="button"
          onClick={() => onChange(EMPTY_FILTER)}
          className="rounded-md border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
        >
          Reset
        </button>
      </div>
    </div>
  );
}

const inputClass =
  "rounded-md border border-slate-200 bg-white px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-950";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
      {label}
      {children}
    </label>
  );
}
